/**
 * Settings > Calendar (IP-104, IP-112; design 2026-10-02 sections 6 and 8),
 * the screen's logic without React, so node --test reads it directly.
 *
 * The calendar is the Core's own (the calendar table, filled by the
 * migrations' seed and by accepted CALENDAR files), read through GET
 * settings/calendar?from&to: the stored dates in the window, the calendar's
 * horizon (its first and last stored date) and the changes accepted files
 * made to dates in the window. The prototype's panel generated weekdays and
 * weekends in the browser and knew no holiday; this one shows what the Core
 * holds, all five day types with the holidays' names - and says "not loaded"
 * for a date it does not hold (beyond the horizon), rather than guess.
 *
 * One month at a time, by month navigation; the server's window is at most
 * 366 days, and a month is well inside it.
 */
import { addDays, colomboBusinessDate, isCalendarDate } from '@lfoc/domain/orders/dealing-date.js';

/** The calendar every fund deals on today (calendar/rules.js CALENDAR_CODE). */
export const CALENDAR_CODE = 'LK_BUSINESS';

/** calendar.day_type (0005), in the order the screen explains them. */
export const DAY_TYPES = Object.freeze(['BUSINESS', 'WEEKEND', 'PUBLIC_HOLIDAY', 'BANK_HOLIDAY', 'SPECIAL_CLOSURE']);

export const DAY_TYPE_LABEL = Object.freeze({
    BUSINESS: 'Business day',
    WEEKEND: 'Weekend',
    PUBLIC_HOLIDAY: 'Public holiday',
    BANK_HOLIDAY: 'Bank holiday',
    SPECIAL_CLOSURE: 'Special closure',
    NOT_LOADED: 'Not loaded'
});

export const DAY_TYPE_COLOUR = Object.freeze({
    BUSINESS: 'green', WEEKEND: 'default', PUBLIC_HOLIDAY: 'red', BANK_HOLIDAY: 'orange', SPECIAL_CLOSURE: 'purple', NOT_LOADED: 'default'
});

/** calendar.source, in words. */
export const SOURCE_LABEL = Object.freeze({ GAZETTE: 'Gazette', CBSL: 'CBSL', OPS: 'Operations', GENERATED: 'Generated', DEV_SEED: 'Dev seed' });

const WEEKDAY = Object.freeze(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']);
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Today's month in Asia/Colombo, YYYY-MM - the server's default window too. */
export const colomboMonth = (at = new Date()) => colomboBusinessDate(at).slice(0, 7);

/** The month n months after a YYYY-MM month. */
export function shiftMonth(month, n) {
    if (!MONTH.test(month)) throw new TypeError('not a YYYY-MM month: ' + String(month));
    const [y, m] = month.split('-').map(Number);
    const index = y * 12 + (m - 1) + n;
    const year = Math.floor(index / 12);
    return String(year).padStart(4, '0') + '-' + String(index - year * 12 + 1).padStart(2, '0');
}

/** The first and last date of a YYYY-MM month. */
export function monthWindow(month) {
    if (!MONTH.test(month)) throw new TypeError('not a YYYY-MM month: ' + String(month));
    const first = month + '-01';
    const last = addDays(shiftMonth(month, 1) + '-01', -1);
    return { from: first, to: last };
}

/** GET settings/calendar's query for a month. */
export const monthQuery = (month, calendarCode = CALENDAR_CODE) => ({ ...monthWindow(month), calendarCode });

/** A month as the heading says it: "October 2026". */
export function monthLabel(month) {
    const [y, m] = month.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** The day of the week of a YYYY-MM-DD date: Mon, Tue... */
export const weekdayOf = (date) => WEEKDAY[new Date(date + 'T00:00:00Z').getUTCDay()];

/** What the stored calendar holds, in words: its first and last date, or that it holds none. */
export function horizonNote(horizon) {
    if (!horizon?.first || !horizon?.last) return 'No calendar dates are loaded yet: upload a CALENDAR file on File Uploads.';
    return 'Loaded from ' + horizon.first + ' to ' + horizon.last + '. A date outside that is not loaded yet: upload a CALENDAR file on File Uploads to extend it.';
}

/**
 * Every date of the month, each as the Core holds it - or NOT_LOADED when it
 * holds none (outside the horizon, or a date the server did not send) - with
 * the changes accepted CALENDAR files made to it.
 *
 * @param {string} month YYYY-MM
 * @param {{ rows?: object[], horizon?: { first: string|null, last: string|null }, changes?: object[] }} answer GET settings/calendar
 * @returns {object[]} one row per date: { key, date, weekday, dayType, isBusinessDay, holidayName, isPublic, source, sourceRef,
 *   loaded, beyondHorizon, changes }
 */
export function monthDays(month, answer) {
    const { from, to } = monthWindow(month);
    const stored = new Map();
    for (const row of answer?.rows ?? []) {
        // The read names it date; an older shape calDate.
        const date = row.date ?? row.calDate;
        if (isCalendarDate(date)) stored.set(date, row);
    }
    const changesOf = new Map();
    for (const change of answer?.changes ?? []) {
        const date = change.date ?? change.calDate;
        if (!changesOf.has(date)) changesOf.set(date, []);
        changesOf.get(date).push(change);
    }
    const first = answer?.horizon?.first ?? null;
    const last = answer?.horizon?.last ?? null;
    const days = [];
    for (let date = from; date <= to; date = addDays(date, 1)) {
        const row = stored.get(date) ?? null;
        const beyondHorizon = !first || !last || date < first || date > last;
        days.push({
            key: date,
            date,
            weekday: weekdayOf(date),
            dayType: row ? row.dayType : 'NOT_LOADED',
            isBusinessDay: row ? row.isBusinessDay === true || row.dayType === 'BUSINESS' : null,
            holidayName: row?.holidayName ?? null,
            isPublic: row ? row.isPublic === true : null,
            source: row?.source ?? null,
            sourceRef: row?.sourceRef ?? null,
            loaded: Boolean(row),
            beyondHorizon,
            changes: changesOf.get(date) ?? []
        });
    }
    return days;
}

/** How many of the month's dates are of each day type (the summary above the table). */
export function dayTypeCounts(days) {
    const counts = Object.fromEntries([...DAY_TYPES, 'NOT_LOADED'].map((t) => [t, 0]));
    for (const d of days) counts[d.dayType] = (counts[d.dayType] ?? 0) + 1;
    return counts;
}

/** A change as a line: "Business day -> Public holiday (Vesak), by Asha, 2 Oct 2026" - the before null for a date the file added. */
export function changeLine(change, when = (iso) => iso ?? '') {
    const state = (s) => (s ? (DAY_TYPE_LABEL[s.dayType] ?? s.dayType) + (s.holidayName ? ' (' + s.holidayName + ')' : '') : 'not loaded');
    const who = change.changedByName ?? change.changedBy ?? null;
    return state(change.before) + ' -> ' + state(change.after) + (who ? ', by ' + who : '') + (change.changedAt ? ', ' + when(change.changedAt) : '');
}
